/**
 * CodeMirror building blocks of `NxCodeEditor`: the token-driven theme and
 * highlight style, the language switch and the completion source. Kept out of
 * the component so they can be tested (and reused) without mounting an editor.
 */
import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from '@codemirror/autocomplete';
import { javascript } from '@codemirror/lang-javascript';
import { PostgreSQL, sql } from '@codemirror/lang-sql';
import { HighlightStyle } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { tags } from '@lezer/highlight';

/** Languages the editor knows by name. Anything else goes through `extensions`. */
export type NxCodeLanguage = 'javascript' | 'json' | 'sql' | 'plain';

/** A completion entry as plain data: the text to insert, and how to show it. */
export interface NxCodeCompletion {
  label: string;
  /** Text inserted instead of `label`. */
  apply?: string;
  /** Short note right of the label (a type, a signature). */
  detail?: string;
  /** Longer explanation, shown beside the list. */
  info?: string;
  /** CodeMirror icon kind: `function`, `variable`, `keyword`, `property`, … */
  type?: string;
}

/**
 * Completions as a list or as a CodeMirror source. A list is matched against
 * the word before the caret (letters, digits, `.`, `_`, `$`); a source gets the
 * full context and decides itself.
 */
export type NxCodeCompletions = readonly NxCodeCompletion[] | CompletionSource;

/**
 * The completion source for `completions`: a function passes through, a list
 * becomes a prefix source over the word before the caret. `null` for none.
 */
export function completionSource(completions?: NxCodeCompletions | null): CompletionSource | null {
  if (!completions) return null;
  if (typeof completions === 'function') return completions;
  const options: Completion[] = completions.map((c) => ({
    label: c.label,
    apply: c.apply,
    detail: c.detail,
    info: c.info,
    type: c.type,
  }));
  if (!options.length) return null;
  return (context: CompletionContext): CompletionResult | null => {
    const word = context.matchBefore(/[\w.$]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    return { from: word.from, options, validFor: /^[\w.$]*$/ };
  };
}

/**
 * Language support by name. JSON uses the JavaScript grammar (a JSON document
 * is a JavaScript expression), which gives strings, numbers, literals and
 * brackets their colours without another dependency. SQL speaks the
 * PostgreSQL dialect, the database every module runs on.
 */
export function languageExtension(language: NxCodeLanguage): Extension[] {
  if (language === 'javascript') return [javascript({ jsx: false, typescript: false })];
  if (language === 'json') return [javascript({ jsx: false, typescript: false })];
  if (language === 'sql') return [sql({ dialect: PostgreSQL, upperCaseKeywords: true })];
  return [];
}

/** Autocompletion for the given completions, or nothing. */
export function completionExtension(completions?: NxCodeCompletions | null): Extension[] {
  const source = completionSource(completions);
  return source ? [autocompletion({ override: [source] })] : [];
}

/** One highlight style over the colour tokens the rest of the app uses. */
export const nxCodeHighlightStyle = HighlightStyle.define([
  {
    tag: [tags.comment, tags.lineComment, tags.blockComment],
    color: 'var(--nxt-fg-muted)',
    fontStyle: 'italic',
  },
  { tag: [tags.string, tags.special(tags.string)], color: 'var(--nxt-hue-teal-fg)' },
  { tag: [tags.number, tags.bool, tags.null], color: 'var(--nxt-hue-amber-fg)' },
  {
    tag: [tags.keyword, tags.modifier, tags.controlKeyword],
    color: 'var(--nxt-hue-violet-fg)',
    fontWeight: '600',
  },
  {
    tag: [tags.function(tags.variableName), tags.function(tags.propertyName)],
    color: 'var(--nxt-hue-sky-fg)',
  },
  {
    tag: [tags.definition(tags.variableName), tags.definition(tags.propertyName)],
    color: 'var(--nxt-fg)',
  },
  { tag: [tags.propertyName, tags.attributeName], color: 'var(--nxt-hue-sky-fg)' },
  { tag: [tags.typeName, tags.className], color: 'var(--nxt-hue-sky-fg)' },
  { tag: tags.operator, color: 'var(--nxt-fg-muted)' },
  { tag: tags.invalid, color: 'var(--nxt-danger-subtle-fg)' },
]);

/**
 * The editor theme, entirely from `--nxt-*` tokens: light and dark follow the
 * app's theme through the CSS variables, no second CodeMirror theme needed.
 */
export const nxCodeTheme = EditorView.theme({
  '&': {
    height: '100%',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 'var(--nxt-font-size-xs)',
    color: 'var(--nxt-fg)',
    backgroundColor: 'var(--nxt-surface-bg)',
  },
  '&.cm-focused': { outline: 'none' },
  '.cm-content': {
    padding: 'var(--nxt-space-xs) 0',
    caretColor: 'var(--nxt-fg)',
  },
  '.cm-scroller': { lineHeight: '1.55', overflow: 'auto' },
  '.cm-gutters': {
    backgroundColor: 'var(--nxt-surface-sunken-bg)',
    color: 'var(--nxt-fg-muted)',
    border: 'none',
    borderRight: 'var(--nxt-hairline) solid var(--nxt-border)',
  },
  '.cm-lineNumbers .cm-gutterElement': { padding: '0 var(--nxt-space-xs) 0 var(--nxt-space-sm)' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent', color: 'var(--nxt-fg)' },
  '.cm-activeLine': {
    backgroundColor: 'color-mix(in srgb, var(--nxt-brand-bg) 6%, transparent)',
  },
  '.cm-errorLine': { backgroundColor: 'var(--nxt-danger-subtle-bg)' },
  '.cm-selectionBackground, &.cm-focused .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'color-mix(in srgb, var(--nxt-brand-bg) 25%, transparent)',
  },
  '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
    backgroundColor: 'color-mix(in srgb, var(--nxt-brand-bg) 18%, transparent)',
    outline: 'none',
  },
  '.cm-selectionMatch': {
    backgroundColor: 'color-mix(in srgb, var(--nxt-warning-bg) 20%, transparent)',
  },
  '.cm-searchMatch': {
    backgroundColor: 'color-mix(in srgb, var(--nxt-warning-bg) 30%, transparent)',
    outline: 'var(--nxt-hairline) solid var(--nxt-warning-border)',
  },
  '.cm-panels': {
    backgroundColor: 'var(--nxt-surface-sunken-bg)',
    color: 'var(--nxt-fg)',
    borderColor: 'var(--nxt-border)',
  },
  '.cm-tooltip': {
    border: 'var(--nxt-hairline) solid var(--nxt-popover-border)',
    borderRadius: 'var(--nxt-radius-md)',
    backgroundColor: 'var(--nxt-popover-bg)',
    color: 'var(--nxt-popover-fg)',
    boxShadow: 'var(--nxt-popover-shadow)',
    overflow: 'hidden',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li': {
    padding: 'var(--nxt-space-2xs) var(--nxt-space-sm)',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    backgroundColor: 'var(--nxt-brand-subtle-bg)',
    color: 'var(--nxt-brand-subtle-fg)',
  },
  '.cm-completionDetail': { color: 'var(--nxt-fg-muted)', fontStyle: 'normal' },
  '.cm-placeholder': { color: 'var(--nxt-fg-muted)' },
  '.cm-foldPlaceholder': {
    backgroundColor: 'var(--nxt-surface-sunken-bg)',
    border: 'var(--nxt-hairline) solid var(--nxt-border)',
    color: 'var(--nxt-fg-muted)',
  },
});
